import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// Stylist interface - matches API response
export interface Stylist {
  id: string;
  franchise_id: string;
  name: string;
  phone_number: string;
  email: string;
  gender: string;
  role: string;
  level: string;
  specialization: string;
  certifications: string;  // JSON string
  languages: string;       // JSON string
  experience_years: number;
  bio: string;
  image_url: string;
  availability: string;    // JSON string
  on_leave: string;
  leave_start_date: string | null;
  leave_end_date: string | null;
  average_rating: number;
  created_by: string;
  updated_by: string | null;
  is_active: number;
  created_date: string;
  updated_date: string;
}

// Franchise info interface
export interface FranchiseInfo {
  franchiseId: string;
  userId: string;
  accountId?: string;
}

// State interface
interface StylistState {
  // Franchise information
  franchiseId: string | null;
  userId: string | null;
  accountId: string | null;
  
  // Stylist data
  stylists: Stylist[];
  isLoading: boolean;
  error: string | null;
}

const initialState: StylistState = {
  franchiseId: null,
  userId: null,
  accountId: null,
  stylists: [],
  isLoading: false,
  error: null,
};

const stylistSlice = createSlice({
  name: "stylist",
  initialState,
  reducers: {
    // Set franchise information
    setFranchiseInfo: (state, action: PayloadAction<FranchiseInfo>) => {
      state.franchiseId = action.payload.franchiseId;
      state.userId = action.payload.userId;
      state.accountId = action.payload.accountId || null;
    },
    
    // Fetch stylists (trigger saga)
    fetchStylists: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    
    // Fetch stylists success
    fetchStylistsSuccess: (state, action: PayloadAction<Stylist[]>) => {
      state.isLoading = false;
      state.stylists = action.payload;
      state.error = null;
    },
    
    // Fetch stylists failure
    fetchStylistsFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    
    // Clear stylist data
    clearStylistData: (state) => {
      state.stylists = [];
      state.error = null;
    },
    
    // Clear all data (including franchise info)
    clearAllData: (state) => {
      state.franchiseId = null;
      state.userId = null;
      state.accountId = null;
      state.stylists = [];
      state.isLoading = false;
      state.error = null;
    },
  },
});

export const {
  setFranchiseInfo,
  fetchStylists,
  fetchStylistsSuccess,
  fetchStylistsFailure,
  clearStylistData,
  clearAllData,
} = stylistSlice.actions;

export default stylistSlice;
